import { useState } from "react";
import { TabPanel } from "@mui/lab";
import { Box, Button, Stack, Typography as T } from "@mui/material";
import DatasetPageLayout from "datasets/components/DatasetPageLayout";
import EmptyStateText from "datasets/components/EmptyStateText";
import usePermissions from "datasets/hooks/usePermissions";
import AddReportModal from "datasets/pages/dashboards/components/AddReportModal";
import DatasetReportList from "datasets/pages/dashboards/components/DatasetReportList";
import useDatasetReportFQNs from "datasets/pages/dashboards/hooks/useDatasetReportFQNs";
import useDatasetReports from "datasets/pages/dashboards/hooks/useDatasetReports";
import { PlusIcon } from "~/assets/icons";
import PageHeader from "~/components/PageHeader";
import { Badge } from "~/components/ui/badge";

const EmptyReports = ({ onClick }: { onClick: () => void }) => {
  const canCreate = usePermissions("create:catalog-report");

  return (
    <Stack alignItems="center" gap={3} mt={12}>
      {canCreate ? (
        <>
          <EmptyStateText>
            This dataset has no dashboards.
            <br /> Add a dashboard below to get started.
          </EmptyStateText>
          <Button startIcon={<PlusIcon />} color="primary" variant="contained" onClick={onClick}>
            Add Dashboard
          </Button>
        </>
      ) : (
        <EmptyStateText>This dataset has no dashboards.</EmptyStateText>
      )}
    </Stack>
  );
};

const DatasetReports = () => {
  const { data: reports, isLoading: loadingReports } = useDatasetReports();
  const { data: reportFQNs } = useDatasetReportFQNs();
  const canCreate = usePermissions("create:catalog-report");
  const [open, setOpen] = useState(false);
  const showEmpty = !loadingReports && reports?.length === 0;

  return (
    <TabPanel value="reports" sx={{ p: 0 }}>
      <DatasetPageLayout bgcolor="common.dimmedPageBackground">
        {showEmpty ? (
          <EmptyReports onClick={() => setOpen(true)} />
        ) : (
          <Stack gap={2} alignItems="flex-start">
            <Box
              display="flex"
              gap={1}
              width="100%"
              minHeight={36}
              alignItems="center"
              justifyContent="space-between"
            >
              <PageHeader lineHeight={1.8}>
                Dashboards
                <Badge variant="primary">BETA</Badge>
              </PageHeader>
              {canCreate && (
                <Button startIcon={<PlusIcon />} variant="contained" onClick={() => setOpen(true)}>
                  Add Dashboard
                </Button>
              )}
            </Box>
            <DatasetReportList />
          </Stack>
        )}
        <AddReportModal open={open} onClose={() => setOpen(false)} reportFQNs={reportFQNs || []} />
      </DatasetPageLayout>
    </TabPanel>
  );
};

export default DatasetReports;
